import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useMatchVisualizations } from '../../../shared/hooks/useMatchVisualizations';
import { useMatchVisualizationMetadata } from '../../../shared/hooks/useMatchVisualizationMetadata';
import { useMatchPeriods } from '../../../shared/hooks/useMatchPeriods';
import { useMapVisualization } from '../../../shared/hooks/useMapVisualization';
import { useTeamShape, TeamShapeConfig } from '../../../shared/hooks/useTeamShape';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { scoresheetService } from '../../../shared/services/scoresheetService';
import { StatType } from '../../../shared/types/database';
import { MapFiltersPanel } from '../../../shared/components/pitch/filters';
import { VisualizationControls } from '../../../shared/components/pitch/controls';
import { MapRenderer, EventSummary, TeamShapeRenderer } from '../../../shared/components/pitch/renderer';
import { flipPointsToFirstHalf, getEventPeriod } from '../../../shared/components/pitch/utils';
import { MatchPeriods } from '../../../shared/hooks/useMatchPeriods';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { supabase } from '../../../shared/lib/supabase';
import { tacticsImagesService, TacticsImage } from '../../../shared/services/tacticsImagesService';
import { svgToImageBlob, findSVGElement } from '../../../shared/utils/svgToImage';
import { toJpeg } from 'html-to-image';
import SaveTacticsModal from '../components/SaveTacticsModal';
import UploadTacticsPrompt from '../components/UploadTacticsPrompt';
import SavedTacticsImagesList from '../components/SavedTacticsImagesList';
import TacticsWatermark from '../components/TacticsWatermark';
import { useToast, ToastContainer } from '../../../shared/components/ui/Toast';

interface MapsTabProps {
  matchId: string;
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName?: string;
  awayTeamName?: string;
  onSaveButtonClick?: () => void; // Callback to expose save handler
}

const MapsTab: React.FC<MapsTabProps> = ({
  matchId,
  homeTeamId,
  awayTeamId,
  homeTeamName = 'Home Team',
  awayTeamName = 'Away Team',
  onSaveButtonClick,
}) => {
  const { user } = useAuth();
  const { toasts, showToast, removeToast } = useToast();
  
  // Data fetching states
  const [statTypes, setStatTypes] = useState<StatType[]>([]);
  const [players, setPlayers] = useState<Array<{ player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string }>>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Tactics save/upload states
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isUploadPromptOpen, setIsUploadPromptOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentTacticsImage, setCurrentTacticsImage] = useState<TacticsImage | null>(null);
  const [editingTacticsImage, setEditingTacticsImage] = useState<TacticsImage | null>(null);
  const [savedImagesRefreshKey, setSavedImagesRefreshKey] = useState(0);
  const [tournamentId, setTournamentId] = useState<string | null>(null);
  const [defaultTacticsName, setDefaultTacticsName] = useState<string>('');
  
  // Refs for SVG capture
  const visualizationRef = useRef<HTMLDivElement>(null);

  // Team Shape configuration (only used when team_shape is selected)
  const [teamShapeConfig, setTeamShapeConfig] = useState<TeamShapeConfig>({
    mode: 'simple',
    playerCount: 11,
    shapeType: [], // Default to empty (all stat types)
    showNetworks: false // Default to hidden
  });

  // Fetch only metadata (stat type IDs, tags, existence) - optimized to reduce egress
  const { statTypeIds: availableStatTypeIds, tags: availableTags, hasData: hasAnyData, loading: metadataLoading } = useMatchVisualizationMetadata(matchId);
  
  // Fetch match periods
  const { periods, availablePeriods, loading: periodsLoading } = useMatchPeriods(matchId);

  // Fetch stat types and players on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [statTypesData, playersData, tournamentResult] = await Promise.all([
          scoresheetService.getStatTypes(),
          scoresheetService.getMatchLineupPlayers(matchId),
          tacticsImagesService.getMatchTournamentId(matchId),
        ]);
        setStatTypes(statTypesData);
        setPlayers(playersData);
        if (tournamentResult.success && tournamentResult.data) {
          setTournamentId(tournamentResult.data);
        }
      } catch (err) {
        console.error('Error fetching filter data:', err);
      } finally {
        setLoadingData(false);
      }
    };

    if (matchId) {
      fetchData();
    }
  }, [matchId]);

  // Initialize map visualization hook once with empty filters initially
  // Filters will be updated as user interacts
  const mapVisualization = useMapVisualization({
    homeTeamId,
    awayTeamId,
    homeTeamName,
    awayTeamName,
    statTypes,
    availableStatTypeIds,
    availableTags,
    players,
    events: [], // Will be updated after we fetch events
  });

  // Convert match_period filter to start_time/end_time
  const filtersWithTimestamps = useMemo(() => {
    if (!mapVisualization.filters.match_period || !periods) {
      return mapVisualization.filters;
    }

    let period = null;
    switch (mapVisualization.filters.match_period) {
      case 'first_half':
        period = periods.firstHalf;
        break;
      case 'second_half':
        period = periods.secondHalf;
        break;
      case 'extra_time_1st_half':
        period = periods.extraTimeFirstHalf;
        break;
      case 'extra_time_2nd_half':
        period = periods.extraTimeSecondHalf;
        break;
      case 'penalty_shootout':
        period = periods.penaltyShootout;
        break;
    }

    if (period && period.startTimestamp && period.endTimestamp) {
      return {
        ...mapVisualization.filters,
        start_time: period.startTimestamp,
        end_time: period.endTimestamp,
      };
    }

    return mapVisualization.filters;
  }, [mapVisualization.filters, periods]);

  const { events: rawEvents, loading, error } = useMatchVisualizations(matchId, filtersWithTimestamps);

  // Track initial load completion
  useEffect(() => {
    if (!loading && !loadingData && !periodsLoading && !metadataLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, loadingData, periodsLoading, metadataLoading, isInitialLoad]);

  // Open save modal handler (defined early for useEffect)
  const handleOpenSaveModal = useCallback(() => {
    if (!user?.id) {
      showToast('Please sign in to save tactics configurations.', 'error');
      return;
    }
    // Generate default name only once when opening modal
    if (!editingTacticsImage) {
      setDefaultTacticsName(`Tactics ${new Date().toLocaleString()}`);
    }
    setIsSaveModalOpen(true);
  }, [user?.id, editingTacticsImage, showToast]);

  // Expose save handler to parent component (must be before early returns)
  useEffect(() => {
    if (onSaveButtonClick) {
      // Store the handler in a way parent can call it
      // Using a ref-like pattern via callback
      (window as any).__mapsTabSaveHandler = handleOpenSaveModal;
    }
    return () => {
      delete (window as any).__mapsTabSaveHandler;
    };
  }, [onSaveButtonClick, handleOpenSaveModal]);

  /**
   * Normalize events to first half perspective if toggle is enabled
   * Flips coordinates for second half and extra time 2nd half events
   */
  const normalizedEvents = useMemo(() => {
    if (!mapVisualization.normalizeToFirstHalf) {
      return [...rawEvents];
    }

    if (!periods) {
      console.warn('Normalize to first half: periods not loaded yet');
      return [...rawEvents];
    }

    return rawEvents.map((event) => {
      if (!event.position_data || event.position_data.length === 0) {
        return { ...event };
      }

      const period = getEventPeriod(event.event_timestamp, periods as MatchPeriods | null);
      const needsTransformation = period === 'second_half' || period === 'extra_time_2nd_half';
      
      if (!needsTransformation) {
        return { ...event };
      }

      const flippedPositionData = flipPointsToFirstHalf(event.position_data);
      return {
        ...event,
        position_data: flippedPositionData,
      };
    });
  }, [rawEvents, mapVisualization.normalizeToFirstHalf, periods, getEventPeriod]);

  // Use normalized events for visualization
  const events = normalizedEvents;

  // Check if team_shape is selected
  const isTeamShapeSelected = mapVisualization.visualizationType.includes('team_shape');

  // Determine if both teams should be shown
  const showBothTeams = useMemo(() => {
    const teamIds = mapVisualization.filters.team_id || [];
    return teamIds.length === 0 || teamIds.length === 2 || 
           (teamIds.includes(homeTeamId) && teamIds.includes(awayTeamId));
  }, [mapVisualization.filters.team_id, homeTeamId, awayTeamId]);

  // Determine selected team for team shape (from filters or default to home)
  // Only used when a single team is selected
  const selectedTeamForShape = useMemo(() => {
    const teamIds = mapVisualization.filters.team_id || [];
    if (teamIds.length === 1) {
      return teamIds[0];
    }
    return homeTeamId; // Default to home team (only used when showBothTeams is false)
  }, [mapVisualization.filters.team_id, homeTeamId]);

  // Home team shape config
  const homeTeamShapeConfigForSingle = useMemo(() => ({
    ...teamShapeConfig,
    attackingDirection: mapVisualization.homeTeamAttackingDirection,
    normalizeToFirstHalf: mapVisualization.normalizeToFirstHalf
  }), [teamShapeConfig, mapVisualization.homeTeamAttackingDirection, mapVisualization.normalizeToFirstHalf]);

  // Away team shape config
  const awayTeamShapeConfigForSingle = useMemo(() => ({
    ...teamShapeConfig,
    attackingDirection: mapVisualization.awayTeamAttackingDirection,
    normalizeToFirstHalf: mapVisualization.normalizeToFirstHalf
  }), [teamShapeConfig, mapVisualization.awayTeamAttackingDirection, mapVisualization.normalizeToFirstHalf]);

  // Calculate team shapes for both teams when "Both Teams" is selected
  // In compare mode or when both teams are selected, use separate shape types if available
  const homeShapeTypes = (mapVisualization.compareMode || showBothTeams) && teamShapeConfig.homeShapeType
    ? teamShapeConfig.homeShapeType
    : teamShapeConfig.shapeType || [];
  const awayShapeTypes = (mapVisualization.compareMode || showBothTeams) && teamShapeConfig.awayShapeType
    ? teamShapeConfig.awayShapeType
    : teamShapeConfig.shapeType || [];

  const { clusters: homeClustersSingle, loading: homeShapeLoading, error: homeShapeError, pointCount: homePointCount } = useTeamShape(
    isTeamShapeSelected && showBothTeams ? events : [],
    homeTeamShapeConfigForSingle,
    isTeamShapeSelected && showBothTeams ? homeTeamId : undefined,
    showBothTeams ? homeShapeTypes : undefined
  );

  const { clusters: awayClustersSingle, loading: awayShapeLoading, error: awayShapeError, pointCount: awayPointCount } = useTeamShape(
    isTeamShapeSelected && showBothTeams ? events : [],
    awayTeamShapeConfigForSingle,
    isTeamShapeSelected && showBothTeams ? awayTeamId : undefined,
    showBothTeams ? awayShapeTypes : undefined
  );

  // Calculate team shape for single team selection
  const selectedTeamShapeConfig = useMemo(() => {
    if (selectedTeamForShape === homeTeamId) {
      return homeTeamShapeConfigForSingle;
    } else if (selectedTeamForShape === awayTeamId) {
      return awayTeamShapeConfigForSingle;
    }
    return homeTeamShapeConfigForSingle;
  }, [selectedTeamForShape, homeTeamId, awayTeamId, homeTeamShapeConfigForSingle, awayTeamShapeConfigForSingle]);

  const selectedTeamShapeTypes = teamShapeConfig.shapeType || [];
  const { clusters, loading: shapeLoading, error: shapeError, pointCount } = useTeamShape(
    isTeamShapeSelected && !showBothTeams ? events : [],
    selectedTeamShapeConfig,
    isTeamShapeSelected && !showBothTeams ? selectedTeamForShape : undefined,
    !showBothTeams ? selectedTeamShapeTypes : undefined
  );

  // Combined loading and error states
  const combinedShapeLoading = shapeLoading || homeShapeLoading || awayShapeLoading;
  const combinedShapeError = shapeError || homeShapeError || awayShapeError;

  // Calculate team shapes for both teams in compare mode
  const homeTeamShapeConfig = useMemo(() => ({
    ...homeTeamShapeConfigForSingle,
    attackingDirection: mapVisualization.homeTeamAttackingDirection,
    formation: mapVisualization.compareMode && teamShapeConfig.mode === 'formation' && teamShapeConfig.homeFormation
      ? teamShapeConfig.homeFormation
      : homeTeamShapeConfigForSingle.formation
  }), [homeTeamShapeConfigForSingle, mapVisualization.homeTeamAttackingDirection, mapVisualization.compareMode, teamShapeConfig.mode, teamShapeConfig.homeFormation]);

  const awayTeamShapeConfig = useMemo(() => ({
    ...awayTeamShapeConfigForSingle,
    attackingDirection: mapVisualization.awayTeamAttackingDirection,
    formation: mapVisualization.compareMode && teamShapeConfig.mode === 'formation' && teamShapeConfig.awayFormation
      ? teamShapeConfig.awayFormation
      : awayTeamShapeConfigForSingle.formation
  }), [awayTeamShapeConfigForSingle, mapVisualization.awayTeamAttackingDirection, mapVisualization.compareMode, teamShapeConfig.mode, teamShapeConfig.awayFormation]);

  const { clusters: homeClusters } = useTeamShape(
    isTeamShapeSelected && mapVisualization.compareMode ? events : [],
    homeTeamShapeConfig,
    isTeamShapeSelected && mapVisualization.compareMode ? homeTeamId : undefined,
    mapVisualization.compareMode ? (teamShapeConfig.homeShapeType || []) : undefined
  );

  const { clusters: awayClusters } = useTeamShape(
    isTeamShapeSelected && mapVisualization.compareMode ? events : [],
    awayTeamShapeConfig,
    isTeamShapeSelected && mapVisualization.compareMode ? awayTeamId : undefined,
    mapVisualization.compareMode ? (teamShapeConfig.awayShapeType || []) : undefined
  );

  // Initial loading state - only show full spinner on first load
  const isInitialLoading = isInitialLoad && (loading || loadingData || periodsLoading || metadataLoading);
  
  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-3">
        <MapFiltersPanel
          mapVisualization={mapVisualization}
          availablePeriods={availablePeriods}
          availableTags={availableTags}
          loadingData={loadingData}
          compareMode={mapVisualization.compareMode}
          showChips={false}
        />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-600 mb-2">Error loading visualizations</div>
            <div className="text-sm text-gray-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state (no data at all)
  if (!hasAnyData) {
    return (
      <div className="space-y-3">
        <MapFiltersPanel
          mapVisualization={mapVisualization}
          availablePeriods={availablePeriods}
          availableTags={availableTags}
          loadingData={loadingData}
          compareMode={mapVisualization.compareMode}
          showChips={false}
        />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-gray-600 mb-2">No visualization data available</div>
            <div className="text-sm text-gray-500">
              This match doesn't have any recorded position data yet.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle team selection change for team shape
  const handleHomeTeamDirectionChange = (direction: 'left' | 'right') => {
    mapVisualization.setHomeTeamAttackingDirection(direction);
    if (mapVisualization.normalizeToFirstHalf) {
      mapVisualization.setAwayTeamAttackingDirection(direction === 'left' ? 'right' : 'left');
    }
  };

  const handleAwayTeamDirectionChange = (direction: 'left' | 'right') => {
    mapVisualization.setAwayTeamAttackingDirection(direction);
    if (mapVisualization.normalizeToFirstHalf) {
      mapVisualization.setHomeTeamAttackingDirection(direction === 'left' ? 'right' : 'left');
    }
  };

  // Save tactics configuration
  const handleSaveTactics = async (name: string, action: 'update' | 'create') => {
    if (!user?.id || !tournamentId) {
      showToast('Please sign in to save tactics configurations.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const visualizationConfig = tacticsImagesService.extractVisualizationConfig(
        mapVisualization,
        homeTeamId,
        awayTeamId,
        homeTeamName,
        awayTeamName,
        isTeamShapeSelected ? teamShapeConfig : undefined
      );
      const viewTypes = tacticsImagesService.extractViewTypes(mapVisualization.visualizationType);

      if (action === 'update' && editingTacticsImage) {
        const result = await tacticsImagesService.updateTacticsImage(editingTacticsImage.id, {
          name,
          visualizationConfig,
          viewTypes,
          compareMode: mapVisualization.compareMode,
          updatedBy: user.id,
        });

        if (!result.success || !result.data) {
          showToast(result.error || 'Failed to update tactics configuration.', 'error');
          return;
        }

        setCurrentTacticsImage(result.data);
        setIsSaveModalOpen(false);
        setEditingTacticsImage(null);
        setSavedImagesRefreshKey(prev => prev + 1);
        
        // Show upload prompt if status is still 'saved'
        if (result.data.status === 'saved') {
          setIsUploadPromptOpen(true);
        }
      } else {
        const result = await tacticsImagesService.createTacticsImage({
          matchId,
          tournamentId,
          name,
          visualizationConfig,
          viewTypes,
          compareMode: mapVisualization.compareMode,
          createdBy: user.id,
        });

        if (!result.success || !result.data) {
          showToast(result.error || 'Failed to save tactics configuration.', 'error');
          return;
        }

        setCurrentTacticsImage(result.data);
        setIsSaveModalOpen(false);
        setSavedImagesRefreshKey(prev => prev + 1);
        
        // Show upload prompt
        setIsUploadPromptOpen(true);
      }
    } catch (error) {
      console.error('Error saving tactics:', error);
      showToast('Failed to save tactics configuration.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Upload tactics image
  const handleUploadTactics = async (tacticsImage?: TacticsImage) => {
    const imageToUpload = tacticsImage || currentTacticsImage;
    if (!imageToUpload || !user?.id || !tournamentId) {
      return;
    }

    setIsUploading(true);
    try {
      // Capture the full visualization card (team names + directions, pitch canvas, legend)
      // visualizationRef wraps MapRenderer or TeamShapeRenderer; firstElementChild is the card root
      const cardElement = visualizationRef.current?.firstElementChild as HTMLElement | null;
      let blob: Blob;
      let format: 'webp' | 'jpeg' = 'jpeg';
      let sizeBytes: number;

      if (cardElement && cardElement.offsetWidth > 0 && cardElement.offsetHeight > 0) {
        // Full card capture: team names + directions above, pitch, legend below (JPEG for smaller size)
        const dataUrl = await toJpeg(cardElement, {
          quality: 0.75,
          pixelRatio: 2,
          backgroundColor: '#ffffff',
        });
        blob = await fetch(dataUrl).then((r) => r.blob());
        sizeBytes = blob.size;
      } else {
        // Fallback: capture only the pitch SVG if card capture is unavailable
        const svgElement = findSVGElement(visualizationRef.current);
        if (!svgElement) {
          showToast('Could not find visualization to capture.', 'error');
          setIsUploading(false);
          return;
        }
        const renderedWidth = svgElement.clientWidth || svgElement.viewBox.baseVal.width || svgElement.getBBox().width || 1050;
        const renderedHeight = svgElement.clientHeight || svgElement.viewBox.baseVal.height || svgElement.getBBox().height || 680;
        const scale = 2;
        const targetWidth = Math.max(renderedWidth * scale, 2100);
        const targetHeight = Math.max(renderedHeight * scale, 1360);
        const result = await svgToImageBlob(svgElement, {
          format: 'webp',
          quality: 0.75,
          width: targetWidth,
          height: targetHeight,
        });
        blob = result.blob;
        format = result.format;
        sizeBytes = result.sizeBytes;
      }

      // Get presigned URL
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/get-r2-tactics-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          tournamentId,
          matchId,
          imageId: imageToUpload.id,
          fileExtension: format,
          contentType: `image/${format}`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const { url, path } = await response.json();

      // Upload blob to R2
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': `image/${format}`,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to R2');
      }

      // Get public URL (construct from CDN base URL)
      // Use environment variable with fallback to match existing CDN pattern
      const cdnBaseUrl = process.env.REACT_APP_R2_PUBLIC_URL || 'https://cdn.capture.kridah.com';
      const imageUrl = `${cdnBaseUrl}/${path}`;

      // Update tactics image with upload info
      const updateResult = await tacticsImagesService.uploadTacticsImage(imageToUpload.id, {
        imageUrl,
        imageStoragePath: path,
        imageFormat: format,
        fileSizeBytes: sizeBytes,
        updatedBy: user.id,
      });

      if (!updateResult.success || !updateResult.data) {
        throw new Error(updateResult.error || 'Failed to update tactics image');
      }

      setCurrentTacticsImage(updateResult.data);
      setIsUploadPromptOpen(false);
      setSavedImagesRefreshKey(prev => prev + 1);
      
      showToast('Image uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading tactics image:', error);
      showToast(error instanceof Error ? error.message : 'Failed to upload image.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  // Load saved tactics configuration
  const handleLoadTactics = (tacticsImage: TacticsImage) => {
    const config = tacticsImage.visualization_config;
    
    // Restore visualization state
    mapVisualization.setVisualizationType(config.visualizationType as any);
    mapVisualization.setPointSelection(config.pointSelection as any);
    mapVisualization.setTrajectoryDensity(config.trajectoryDensity as any);
    mapVisualization.setColorBy(config.colorBy);
    mapVisualization.setStatTypeVisualizationMap(config.statTypeVisualizationMap as any);
    mapVisualization.setFilters(config.filters as any);
    mapVisualization.setNormalizeToFirstHalf(config.normalizeToFirstHalf);
    mapVisualization.setCompareMode(config.compareMode);
    mapVisualization.setHomeTeamAttackingDirection(config.homeTeamAttackingDirection);
    mapVisualization.setAwayTeamAttackingDirection(config.awayTeamAttackingDirection);
    mapVisualization.setWhiteBackground(config.whiteBackground);
    
    if (config.heatmapRadius !== undefined) mapVisualization.setHeatmapRadius(config.heatmapRadius);
    if (config.heatmapSmoothness !== undefined) mapVisualization.setHeatmapSmoothness(config.heatmapSmoothness);
    if (config.heatmapOpacity !== undefined) mapVisualization.setHeatmapOpacity(config.heatmapOpacity);
    if (config.gridSize !== undefined) mapVisualization.setGridSize(config.gridSize);
    if (config.gridShowLabels !== undefined) mapVisualization.setGridShowLabels(config.gridShowLabels);
    if (config.gridOpacity !== undefined) mapVisualization.setGridOpacity(config.gridOpacity);
    if (config.scatterShapes) mapVisualization.setScatterShapes(config.scatterShapes as any);
    if (config.trajectoryStyles) mapVisualization.setTrajectoryStyles(config.trajectoryStyles as any);
    if (config.teamColors) mapVisualization.setTeamColors(config.teamColors);
    if (config.statTypeColors) mapVisualization.setStatTypeColors(config.statTypeColors);
    
    // Restore team shape config if applicable
    if (config.teamShapeConfig) {
      setTeamShapeConfig(config.teamShapeConfig);
    }

    setEditingTacticsImage(tacticsImage);
    
    // If status is 'saved', show upload prompt option
    if (tacticsImage.status === 'saved') {
      setCurrentTacticsImage(tacticsImage);
    }

    // Auto-scroll to pitch canvas to show the loaded configuration
    // Use setTimeout to ensure DOM has updated with new configuration
    setTimeout(() => {
      if (visualizationRef.current) {
        visualizationRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  // Delete tactics image
  const handleDeleteTactics = async (tacticsImageId: string) => {
    if (!user?.id) return;

    try {
      const result = await tacticsImagesService.deleteTacticsImage(tacticsImageId, user.id);
      if (result.success) {
        setSavedImagesRefreshKey(prev => prev + 1);
        if (editingTacticsImage?.id === tacticsImageId) {
          setEditingTacticsImage(null);
        }
        showToast('Tactics configuration deleted successfully.', 'success');
      } else {
        showToast(result.error || 'Failed to delete tactics configuration.', 'error');
      }
    } catch (error) {
      console.error('Error deleting tactics:', error);
      showToast('Failed to delete tactics configuration.', 'error');
    }
  };

  // Edit tactics image name
  const handleEditTactics = async (tacticsImageId: string, newName: string) => {
    if (!user?.id) return;

    try {
      const result = await tacticsImagesService.updateTacticsImage(tacticsImageId, {
        name: newName,
        updatedBy: user.id,
      });

      if (result.success && result.data) {
        setSavedImagesRefreshKey(prev => prev + 1);
        // Update editingTacticsImage if it's the one being edited
        if (editingTacticsImage?.id === tacticsImageId) {
          setEditingTacticsImage(result.data);
        }
        showToast('Tactics configuration name updated successfully.', 'success');
      } else {
        showToast(result.error || 'Failed to update tactics configuration name.', 'error');
        throw new Error(result.error || 'Failed to update tactics configuration name.');
      }
    } catch (error) {
      console.error('Error updating tactics name:', error);
      showToast('Failed to update tactics configuration name.', 'error');
      throw error;
    }
  };


  return (
    <div className="space-y-3">
      {/* Saved Tactics Images List */}
      <SavedTacticsImagesList
        matchId={matchId}
        onLoadTactics={handleLoadTactics}
        onUploadTactics={handleUploadTactics}
        onDeleteTactics={handleDeleteTactics}
        onEditTactics={handleEditTactics}
        refreshKey={savedImagesRefreshKey}
      />

      {/* Filters */}
      <MapFiltersPanel
        mapVisualization={mapVisualization}
        availablePeriods={availablePeriods}
        availableTags={availableTags}
        loadingData={loadingData}
        compareMode={mapVisualization.compareMode}
        showChips={true}
        disableFiltersForTeamShape={isTeamShapeSelected}
      />

      {/* Visualization Controls */}
      <VisualizationControls
        mapVisualization={mapVisualization}
        statTypes={statTypes}
        teamShapeConfig={teamShapeConfig}
        onTeamShapeConfigChange={setTeamShapeConfig}
        teamShapeLoading={combinedShapeLoading}
        teamShapePointCount={showBothTeams ? (homePointCount + awayPointCount) : pointCount}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
        showBothTeams={isTeamShapeSelected && showBothTeams}
      />

      {/* Render Team Shape or Map Renderer based on selection. Wrapper is relative so watermark is captured in export. */}
      <div ref={visualizationRef}>
      {isTeamShapeSelected ? (
        <div className="relative">
          <TeamShapeRenderer
            compareMode={mapVisualization.compareMode}
            homeClusters={homeClusters}
            awayClusters={awayClusters}
            showBothTeams={showBothTeams}
            homeClustersSingle={homeClustersSingle}
            awayClustersSingle={awayClustersSingle}
            clusters={clusters}
            selectedTeamForShape={selectedTeamForShape}
            mapVisualization={mapVisualization}
            statTypes={statTypes}
            events={events}
            homeTeamId={homeTeamId}
            awayTeamId={awayTeamId}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            handleHomeTeamDirectionChange={handleHomeTeamDirectionChange}
            handleAwayTeamDirectionChange={handleAwayTeamDirectionChange}
            showNetworks={teamShapeConfig.showNetworks}
            combinedShapeLoading={combinedShapeLoading}
            combinedShapeError={combinedShapeError}
            homePointCount={homePointCount}
            awayPointCount={awayPointCount}
            pointCount={pointCount}
          />
          <TacticsWatermark />
        </div>
      ) : (
        <>
          <div className="relative">
            <MapRenderer
              events={events}
              mapVisualization={mapVisualization}
              statTypes={statTypes}
              homeTeamId={homeTeamId}
              awayTeamId={awayTeamId}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
              handleClearFilters={mapVisualization.handleClearFilters}
              isLoading={loading && !isInitialLoad}
            />
            <TacticsWatermark />
          </div>
          <EventSummary events={events} />
        </>
      )}
      </div>

      {/* Save Modal */}
      <SaveTacticsModal
        isOpen={isSaveModalOpen}
        initialName={editingTacticsImage?.name || defaultTacticsName}
        isSaving={isSaving}
        isEditingExisting={!!editingTacticsImage}
        tacticsImageStatus={editingTacticsImage?.status}
        onClose={() => {
          setIsSaveModalOpen(false);
          setEditingTacticsImage(null);
          setDefaultTacticsName(''); // Reset default name when closing
        }}
        onSave={handleSaveTactics}
      />

      {/* Upload Prompt */}
      <UploadTacticsPrompt
        isOpen={isUploadPromptOpen}
        isUploading={isUploading}
        onClose={() => setIsUploadPromptOpen(false)}
        onUpload={() => handleUploadTactics()}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default MapsTab;
